import React, { useState, useEffect } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import classNames from "classnames"
import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline"
import * as DateFns from "date-fns"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRedo, faUndo } from "@fortawesome/free-solid-svg-icons"
import useOutsideClick from "../../hooks/useOutsideClick"
import { useAuth } from "../../hooks/auth"
import { useNana } from "../../hooks/useNana"
import Button from "../../components/button"

const NAV_ITEMS = [
  { to: "/app/now", label: "Now", testId: "navbar-now", end: true },
  { to: "/app", label: "Mosaic", testId: "navbar-mosaic", end: true },
  { to: "/app/capture", label: "Capture", testId: "navbar-capture", end: true },
  { to: "/app/sources", label: "Sources", testId: "navbar-sources", end: true },
  {
    to: "/app/projects",
    label: "Projects",
    testId: "navbar-projects",
    end: true,
  },
] as const

const desktopLinkClasses = ({ isActive }: { isActive: boolean }) =>
  classNames(
    "relative items-center rounded-full px-3.5 py-1.5 text-[13px] tracking-wide transition-[color,background-color,box-shadow,transform] duration-200 ease-out md:px-4",
    {
      "flex bg-white/90 font-semibold text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.06] dark:bg-white/[0.18] dark:text-white dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_6px_16px_rgba(0,0,0,0.25)] dark:ring-white/20":
        isActive,
      "flex font-medium text-gray-500 hover:bg-white/40 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.08] dark:hover:text-gray-100":
        !isActive,
    },
  )

const menuItemClasses = ({ isActive }: { isActive: boolean }) =>
  classNames(
    "flex min-h-[44px] w-full cursor-pointer items-center px-3.5 py-2.5 text-[13px] tracking-wide transition-colors first:rounded-t-[0.65rem] last:rounded-b-[0.65rem]",
    {
      "bg-black/[0.06] font-semibold text-gray-900 dark:bg-white/[0.12] dark:text-white":
        isActive,
      "font-medium text-gray-600 hover:bg-black/[0.04] hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/[0.06] dark:hover:text-white":
        !isActive,
    },
  )

const menuPanelClasses =
  "z-50 origin-top overflow-hidden rounded-xl border border-black/[0.06] bg-white/95 py-1 shadow-[0_12px_40px_rgba(15,23,42,0.18)] backdrop-blur-xl focus:outline-none dark:border-white/10 dark:bg-gray-900/95 dark:shadow-[0_12px_40px_rgba(0,0,0,0.55)]"

const currentNavLabel = (pathname: string): string => {
  if (pathname.startsWith("/app/now")) return "Now"
  if (pathname.startsWith("/app/capture")) return "Capture"
  if (pathname.startsWith("/app/sources")) return "Sources"
  if (pathname.startsWith("/app/projects")) return "Projects"
  if (pathname.startsWith("/app/account")) return "Account"
  return "Mosaic"
}

export default function Home({ children }) {
  const { logout } = useAuth()
  const { commander, hub } = useNana()
  const navigate = useNavigate()
  const location = useLocation()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [navMenuOpen, setNavMenuOpen] = useState(false)
  const [_, setTick] = useState(new Date())
  const [offlineAt, setOfflineAt] = useState<Date | null>(null)
  const [syncErrorCount, setSyncErrorCount] = useState(0)

  const logoutAndRedirect = () => {
    logout().then(() => {
      navigate("/app/login")
    })
  }

  const profileMenuRef = useOutsideClick(() => {
    setProfileMenuOpen(false)
    return true
  }, profileMenuOpen)

  const navMenuRef = useOutsideClick(() => {
    setNavMenuOpen(false)
    return true
  }, navMenuOpen)

  const refreshUndoState = () => {
    setTick(new Date())
  }

  useEffect(() => {
    const keys = [
      hub.onAny(() => refreshUndoState()),
      hub.on("sync:online-status", ({ offlineAt, syncErrorCount }) => {
        setOfflineAt(offlineAt)
        setSyncErrorCount(syncErrorCount)
      }),
    ]
    refreshUndoState()
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [])

  useEffect(() => {
    setNavMenuOpen(false)
    setProfileMenuOpen(false)
  }, [location.pathname])

  return (
    <div className="relative flex h-full min-h-0 flex-auto flex-col">
      <nav className="absolute inset-x-0 top-0 z-20 border-b border-white/40 bg-white/55 shadow-sm backdrop-blur-xl backdrop-saturate-150 hidden-if-keyboard webkit-app-drag select-none dark:border-white/10 dark:bg-gray-900/55 dark:shadow-none">
        <div className="desktop-traffic-light-inset desktop-window-controls-inset mx-auto flex h-16 justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 md:gap-8">
            <div className="flex flex-shrink-0 items-center text-[13px] font-semibold uppercase tracking-[0.22em] text-gray-800 dark:text-gray-100">
              NANA
            </div>

            {/* Desktop segmented control */}
            <div className="hidden items-center rounded-full bg-black/[0.045] p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.05] dark:bg-white/[0.07] dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)] dark:ring-white/10 md:flex">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={desktopLinkClasses}
                  data-testid={item.testId}
                  end={item.end}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Mobile route switcher pill */}
            <div className="relative webkit-app-no-drag md:hidden" ref={navMenuRef}>
              <div className="flex items-center rounded-full bg-black/[0.045] p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.05] dark:bg-white/[0.07] dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)] dark:ring-white/10">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={navMenuOpen}
                  aria-label="Switch section"
                  onClick={() => setNavMenuOpen(!navMenuOpen)}
                  className="flex min-h-[44px] min-w-[8.5rem] items-center justify-between gap-2 rounded-full bg-white/90 px-4 py-2.5 text-[13px] font-semibold tracking-wide text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.06] transition-colors dark:bg-white/[0.18] dark:text-white dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_6px_16px_rgba(0,0,0,0.25)] dark:ring-white/20"
                >
                  <span>{currentNavLabel(location.pathname)}</span>
                  <ChevronDownIcon
                    className={classNames(
                      "h-4 w-4 shrink-0 text-gray-500 transition-transform dark:text-gray-300",
                      { "rotate-180": navMenuOpen },
                    )}
                  />
                </button>
              </div>

              {navMenuOpen && (
                <div
                  className={classNames(
                    menuPanelClasses,
                    "absolute left-0 mt-2 w-44",
                  )}
                  role="menu"
                  aria-label="Sections"
                >
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className={menuItemClasses}
                      end={item.end}
                      onClick={() => setNavMenuOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="right-0 flex items-center webkit-app-no-drag sm:ml-6 sm:pr-0 md:pr-2">
            <div className="relative" ref={profileMenuRef}>
              <div className="flex items-center gap-2 md:gap-8">
                {offlineAt && (
                  <div className="text-xs">
                    Offline for{" "}
                    {DateFns.differenceInMinutes(new Date(), offlineAt)}m
                  </div>
                )}
                {syncErrorCount > 0 && (
                  <div className="flex flex-col items-center text-xs">
                    <div>Sync error</div>
                    <div>({syncErrorCount} attempts)</div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => commander.undo()}
                    disabled={!commander.canUndo()}
                  >
                    <FontAwesomeIcon icon={faUndo} className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => commander.redo()}
                    disabled={!commander.canRedo()}
                  >
                    <FontAwesomeIcon icon={faRedo} className="h-4 w-4" />
                  </Button>
                </div>
                <button
                  type="button"
                  className="flex items-center rounded-full p-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  id="user-menu-button"
                  aria-expanded={profileMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  data-testid="profile-menu-button"
                >
                  <UserCircleIcon className="w-6 text-gray-600 hover:text-sky-500 dark:text-gray-200 dark:hover:text-sky-200" />
                </button>
              </div>

              {profileMenuOpen && (
                <div
                  className={classNames(
                    menuPanelClasses,
                    "absolute right-0 mt-2 w-48",
                  )}
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                  tabIndex={-1}
                >
                  <NavLink
                    to="/app/account"
                    role="menuitem"
                    className={menuItemClasses}
                    data-testid="dropdown-account-button"
                    onClick={() => setProfileMenuOpen(false)}
                    end
                  >
                    Account
                  </NavLink>
                  <a
                    href="/help"
                    target="_blank"
                    rel="noreferrer"
                    role="menuitem"
                    className={menuItemClasses({ isActive: false })}
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    Help
                  </a>
                  <div
                    className={menuItemClasses({ isActive: false })}
                    role="menuitem"
                    tabIndex={-1}
                    id="user-menu-item-2"
                    onClick={logoutAndRedirect}
                    data-testid="logout-button"
                  >
                    Log out
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      {children}
    </div>
  )
}
